/**
 * PasswordResetModal — the fleet's one password reset: email → six-digit code → new password.
 *
 * Every tenant `<LoginShell>`, console, the marketing logins and planner-web's platform-admin
 * `<LoginForm>` open this modal, and each wires the four callbacks through
 * `usePasswordResetClient({ basePath })` (`web/require-login-shell-password-reset` refuses a mount
 * that does not). The state machine is `usePasswordResetFlow`; each step is its own component under
 * `./passwordReset/`, and each moves focus to its own field on arrival — the dialog's content is
 * REPLACED between steps, and focus left on a removed node falls to `<body>`.
 *
 * 🔴 UNREACHABLE-MODULE-BURNDOWN-001 L16 rebuilt this modal after deleting palm-web's app-local
 * reset tree, which had been unreachable for months with 11 green suites. What that tree had and
 * this lacked — a resend countdown synced to the server, a live requirement checklist, show/hide,
 * "use a different email", translated refusals, a step indicator, per-step focus — was built here
 * for every app at once. What this modal had and should never have had was deleted: a `simple`
 * flow and three no-callback fallbacks that REPORTED RESETS THAT NEVER HAPPENED, and a server
 * `redirectUrl` hop no reset route has ever answered with. See `usePasswordResetFlow` for each.
 *
 * @module @puffin-core/login-shell/components/PasswordResetModal
 */

import PropTypes from 'prop-types';
// 🔴 The NARROW subpath, never the barrel. `@puffin-core/page-shell`'s index pulls
// `react-router-dom` (through `useManageAccountAction`), and login-shell is deliberately
// router-free — importing the barrel here dragged the router in and took 21 of this
// package's suites down with `ReferenceError: TextEncoder is not defined`.
import { Modal } from '@puffin-core/page-shell/modal';
import { useLoginTranslate } from '../hooks/useLoginTranslate.js';
import {
  usePasswordResetFlow,
  PasswordResetStep,
  DEFAULT_RESEND_COOLDOWN_SECONDS,
} from '../hooks/usePasswordResetFlow.js';
import { ResetEmailStep } from './passwordReset/ResetEmailStep.jsx';
import { ResetCodeStep } from './passwordReset/ResetCodeStep.jsx';
import { ResetNewPasswordStep } from './passwordReset/ResetNewPasswordStep.jsx';
import { ResetSuccess } from './passwordReset/ResetSuccess.jsx';

export { PasswordResetStep };

/**
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {string} [props.initialEmail] - pre-fills step 1 from the sign-in form
 * @param {string} [props.initialSubdomain] - the tenant the reset is for
 * @param {(email: string, subdomain: string) => Promise<any>} props.onInitiate
 * @param {(email: string, subdomain: string, code: string) => Promise<{resetToken: string}>} props.onVerify
 * @param {(resetToken: string, newPassword: string) => Promise<any>} props.onComplete
 * @param {(email: string, subdomain: string) => Promise<any>} props.onResend
 * @param {() => void} [props.onSuccess] - called once the new password is saved
 * @param {number} [props.resendCooldownSeconds=60] - the router's resend window; a refusal's
 *   `retryAfter` overrides it
 * @param {React.ReactNode} [props.captchaSlot]
 * @param {Function} [props.t]
 * @returns {React.JSX.Element|null}
 */
export function PasswordResetModal({
  isOpen,
  onClose,
  initialEmail = '',
  initialSubdomain = '',
  onInitiate,
  onVerify,
  onComplete,
  onResend,
  onSuccess,
  resendCooldownSeconds = DEFAULT_RESEND_COOLDOWN_SECONDS,
  captchaSlot = null,
  t,
}) {
  const flow = usePasswordResetFlow({
    initialEmail,
    initialSubdomain,
    onInitiate,
    onVerify,
    onComplete,
    onResend,
    onSuccess,
    resendCooldownSeconds,
  });
  const translate = useLoginTranslate(t);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onOpen={flow.handleOpen}
      title={translate('modal.passwordReset.title')}
      closeLabel={translate('modal.closeLabel')}
      testId="password-reset-modal"
    >
      {renderStep(flow, translate, captchaSlot, onClose)}
    </Modal>
  );
}

/**
 * The body for the current step. Module scope so the modal's own return stays a single element.
 *
 * @returns {React.JSX.Element}
 */
function renderStep(flow, translate, captchaSlot, onClose) {
  switch (flow.state.step) {
    case PasswordResetStep.CODE:
      return <ResetCodeStep flow={flow} translate={translate} />;
    case PasswordResetStep.NEW_PASSWORD:
      return <ResetNewPasswordStep flow={flow} translate={translate} />;
    case PasswordResetStep.SUCCESS:
      return <ResetSuccess onClose={onClose} translate={translate} />;
    default:
      return <ResetEmailStep flow={flow} translate={translate} captchaSlot={captchaSlot} />;
  }
}

PasswordResetModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialEmail: PropTypes.string,
  initialSubdomain: PropTypes.string,
  onInitiate: PropTypes.func.isRequired,
  onVerify: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onResend: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  resendCooldownSeconds: PropTypes.number,
  captchaSlot: PropTypes.node,
  t: PropTypes.func,
};

export default PasswordResetModal;
